import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Container,
  Paper
} from '@mui/material';
import { Person } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import { useUser } from '../../contexts/UserContext';
import { apiService } from '../../config/api';
import { formatPhoneNumber } from '../../utils/textUtils';

interface RegistrationFormData {
  email: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  phoneNumber: string;
  institution: string;
  position: string;
  description: string;
}

const ResearcherRegistration: React.FC = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useSecureAuth();
  const [formData, setFormData] = useState<RegistrationFormData>({
    email: '',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    phoneNumber: '',
    institution: '',
    position: '',
    description: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkingMaster, setCheckingMaster] = useState(true);

  // FIRST PRIORITY: Check if master account and redirect immediately
  useEffect(() => {
    const checkMasterAccount = async () => {
      if (currentUser) {
        try {
          const token = await apiService.getAuthToken();
          if (!token) {
            throw new Error('No authentication token available');
          }
          const masterResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/check-master-status`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': `application/json`
            }
          });
          
          if (masterResponse.ok) {
            const masterData = await masterResponse.json();
            if (masterData.success && masterData.isMasterAccount) {
              navigate('/master/dashboard');
              return;
            }
            }
        } catch (error) {
          }
      }
      setCheckingMaster(false);
    };
    
    checkMasterAccount();
  }, [currentUser, navigate]);

  // Auto-populate email and name for authenticated users
  useEffect(() => {
    if (currentUser) {
      setFormData(prev => ({
        ...prev,
        email: currentUser.email || '',
        firstName: currentUser.displayName?.split(' ')[0] || '',
        lastName: currentUser.displayName?.split(' ').slice(1).join(' ') || ''
      }));
    }
  }, [currentUser]);

  // Use UserContext for status checking
  const { userProfile, loading: userLoading, refreshProfile } = useUser();

  // Check user status and redirect appropriately
  useEffect(() => {
    if (!userLoading && !checkingMaster) {
      // If no profile loaded yet, keep checking (don't redirect)
      if (!userProfile) {
        return;
      }
      
      // Master account - redirect to master dashboard
      if (userProfile.role === 'master' || userProfile.isMasterAccount) {
        navigate('/master/dashboard');
        return;
      }
      
      // Approved researchers should go to researcher dashboard
      if (userProfile.status === 'approved') {
        navigate('/researcher/dashboard');
        return;
      }
      
      // Pending researchers should see application status
      if (userProfile.status === 'pending') {
        navigate('/researcher/application-status');
        return;
      }
      
      // Only noprofile and rejected users should see the application form
      // If status is something else we don't recognize, allow form to show
      if (userProfile.status && userProfile.status !== 'noprofile' && userProfile.status !== 'rejected') {
        // Unknown status - allow form to render
      }
    }
  }, [userProfile, userLoading, checkingMaster, navigate]);

  const handleInputChange = (field: keyof RegistrationFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = field === 'phoneNumber' 
      ? formatPhoneNumber(event.target.value)
      : event.target.value;
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await apiService.post('/api/admin/researcher-applications', formData);
      
      if (response.success) {
        // Refresh user profile to update status from 'noprofile' to 'pending'
        await refreshProfile();
        
        // Immediately redirect to application status page
        navigate('/researcher/application-status', { 
          state: { applicationId: response.applicationId },
          replace: true
        });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to submit application. Please try again.');
      setLoading(false);
    }
  };

  const isFormValid = () => {
    return (
      formData.email.trim() !== '' &&
      formData.firstName.trim() !== '' &&
      formData.lastName.trim() !== '' &&
      formData.dateOfBirth.trim() !== '' &&
      formData.phoneNumber.trim() !== '' &&
      formData.institution.trim() !== '' &&
      formData.position.trim() !== ''
    );
  };


  // Don't render anything while checking master status or user profile
  if (checkingMaster || userLoading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Person sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" component="h1" gutterBottom color="primary">
            Researcher Registration
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Apply to join StudySync as a researcher
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
          Please fill out this form to apply for researcher access to StudySync.
          All applications are reviewed by our administrative team.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Personal Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="primary">
                  Personal Information
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="First Name"
                  value={formData.firstName}
                  onChange={handleInputChange('firstName')}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Last Name"
                  value={formData.lastName}
                  onChange={handleInputChange('lastName')}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  type="email"
                  label="Email Address"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  disabled={!!currentUser?.email}
                  helperText={currentUser?.email ? "Email from your authenticated account" : ""}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  type="date"
                  label="Date of Birth"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange('dateOfBirth')}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Phone Number"
                  value={formData.phoneNumber}
                  onChange={handleInputChange('phoneNumber')}
                  placeholder="(000)-0000-000"
                />
              </Grid>

              {/* Institution Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 2 }}>
                  Institution Information
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Institution/University"
                  value={formData.institution}
                  onChange={handleInputChange('institution')}
                  placeholder="e.g., Stanford University, Google Research, etc."
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Position/Role"
                  value={formData.position}
                  onChange={handleInputChange('position')}
                  placeholder="e.g., Principal Investigator, Graduate Student, Postdoc, etc."
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Description (Optional)"
                  value={formData.description}
                  onChange={handleInputChange('description')}
                  placeholder="Tell us about your research background, current projects, or why you need access to StudySync..."
                />
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12} sx={{ mt: 3 }}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={!isFormValid() || loading}
                  sx={{ py: 1.5 }}
                >
                  {loading ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Submitting Application...
                    </>
                  ) : (
                    'Submit Application'
                  )}
                </Button>
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" align="center">
                  By submitting this application, you agree to use StudySync in accordance with 
                  research ethics guidelines and institutional policies.
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>
    );
  };

export default ResearcherRegistration;
